import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { subscriptionAPI } from '../services/api';
import { authService } from '../services/auth';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [quotaChecks, setQuotaChecks] = useState({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const queryClient = useQueryClient();

  // Check authentication status on mount and window focus
  useEffect(() => {
    const checkAuth = () => {
      const authStatus = authService.isAuthenticated();
      setIsAuthenticated(authStatus);
    };

    // Check immediately on mount
    checkAuth();

    // Re-check when window gains focus (user returns to tab)
    const handleFocus = () => checkAuth();
    window.addEventListener('focus', handleFocus);

    // Also check on storage events (login/logout in other tabs)
    const handleStorage = (e) => {
      if (e.key === 'authToken') {
        checkAuth();
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  // Fetch subscription status
  const { data: status, isLoading: statusLoading, error: statusError } = useQuery({
    queryKey: ['subscription-status'],
    queryFn: () => subscriptionAPI.getStatus(),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch pricing plans
  // eslint-disable-next-line no-unused-vars
  const { data: pricingPlans, isLoading: plansLoading, error: plansError } = useQuery({
    queryKey: ['pricing-plans'],
    queryFn: () => subscriptionAPI.getPricingPlans(),
    enabled: isAuthenticated,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // Upgrade plan mutation
  const upgradeMutation = useMutation({
    mutationFn: (planId) => subscriptionAPI.upgradePlan(planId),
    onSuccess: () => {
      // Refresh subscription status
      queryClient.invalidateQueries(['subscription-status']);
      // Show success message
      // You can integrate with your toast system here
    },
    onError: (error) => {
      // Handle error
      console.error('Failed to upgrade plan:', error);
    },
  });

  // Check quota for a specific module
  const checkQuota = async (moduleName) => {
    try {
      const quota = await subscriptionAPI.checkQuota(moduleName);
      setQuotaChecks(prev => ({
        ...prev,
        [moduleName]: quota
      }));
      return quota;
    } catch (error) {
      console.error(`Failed to check quota for ${moduleName}:`, error);
      return null;
    }
  };

  // Check if user can create items in a module
  const canCreate = (moduleName) => {
    const quota = quotaChecks[moduleName];
    if (!quota) return true; // Allow if quota not checked yet
    return quota.can_create;
  };

  // Get remaining quota for a module
  const getRemainingQuota = (moduleName) => {
    const quota = quotaChecks[moduleName];
    if (!quota) return null;
    return quota.remaining_quota;
  };

  // Get current usage for a module
  const getCurrentUsage = (moduleName) => {
    const quota = quotaChecks[moduleName];
    if (!quota) return 0;
    return quota.current_usage;
  };

  // Get quota limit for a module
  const getQuotaLimit = (moduleName) => {
    const quota = quotaChecks[moduleName];
    if (!quota) return null;
    return quota.quota_limit;
  };

  // Check if plan is unlimited
  const isUnlimited = (moduleName) => {
    const quota = quotaChecks[moduleName];
    if (!quota) return false;
    return quota.is_unlimited;
  };

  // Get current plan type
  const getCurrentPlanType = () => {
    return status?.current_plan?.plan_type || 'free';
  };

  // Check if current plan is free
  const isFreePlan = () => {
    return getCurrentPlanType() === 'free';
  };

  // Check if current plan is paid
  const isPaidPlan = () => {
    const planType = getCurrentPlanType();
    return planType === 'monthly' || planType === 'yearly';
  };

  // Get usage percentage for a module
  const getUsagePercentage = (moduleName) => {
    const quota = quotaChecks[moduleName];
    if (!quota || quota.is_unlimited || !quota.quota_limit) return 0;
    return Math.round((quota.current_usage / quota.quota_limit) * 100);
  };

  // Pre-check quotas for common modules
  useEffect(() => {
    if (isAuthenticated && status) {
      // Check quotas for all modules
      const modulesToCheck = [
        'students', 'batches', 'enrollments', 'enquiries',
        'workshops', 'tasks', 'fee_structures', 'payments', 'invitations'
      ];
      modulesToCheck.forEach(module => {
        checkQuota(module);
      });
    }
  }, [isAuthenticated, status]);

  // Update subscription status when data changes
  useEffect(() => {
    if (status) {
      setSubscriptionStatus(status);
    }
  }, [status]);

  const value = {
    // State
    subscriptionStatus,
    pricingPlans,
    quotaChecks,
    
    // Loading states
    statusLoading,
    plansLoading,
    
    // Errors
    statusError,
    
    // Actions
    upgradePlan: upgradeMutation.mutate,
    checkQuota,
    refreshStatus: () => queryClient.invalidateQueries(['subscription-status']),
    
    // Quota helpers
    canCreate,
    getRemainingQuota,
    getCurrentUsage,
    getQuotaLimit,
    isUnlimited,
    getUsagePercentage,
    
    // Plan helpers
    getCurrentPlanType,
    isFreePlan,
    isPaidPlan,
    
    // Mutation state
    isUpgrading: upgradeMutation.isPending,
    upgradeError: upgradeMutation.error,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}; 